import React from 'react'
import {
  extendTheme,
  ChakraProvider,
  ResponsiveValue,
  SystemProps,
} from '@chakra-ui/react'
import { Global as EmotionGlobal } from '@emotion/react'
import { ChakraTheme } from '@chakra-ui/theme'
import { LinkProps } from 'react-router-dom'
import Link from 'next/link'
import { theme as defaultTheme } from './theme'
import { styles } from './styles'

export type Link = React.FC<LinkProps> | typeof Link

interface ProviderProps {
  theme?: ChakraTheme
  resetCss?: boolean
  Link?: Link
}

// Default to 'sm' (mobile first) as a guess for SSR/SSG
export const DEFAULT_BREAKPOINT = 'sm'
const extensions: {
  components: Record<string, Record<string, unknown>>
  styles: Record<string, Record<string, unknown>>
} = {
  components: {},
  styles,
}
export const injectTheme = (
  componentName: string,
  extension: Record<string, unknown>
): void => {
  if (extensions.components[componentName]) {
    console.trace(
      `Tried to extend Hub theme with component that has already had it's them injected: ${componentName}`
    )
  } else {
    extensions.components[componentName] = extension
  }
}

export const HubLinkContext = React.createContext<Link | undefined>(undefined)

// TODO: Italic & BoldItalic font faces
export const HubProvider: React.FC<ProviderProps> = ({
  children,
  theme,
  resetCss = true,
  Link,
}) => {
  const extendedTheme = extendTheme(extensions, theme ?? (defaultTheme as any))
  return (
    <HubLinkContext.Provider value={Link}>
      <ChakraProvider resetCSS={resetCss} theme={extendedTheme}>
        {/* NOTE: These global styles must be inside the chakra provider so they
      can override the global CSS Chakra injects when selectors have the same
      specificity */}
        <EmotionGlobal
          styles={`
          @font-face {
            font-family: 'Cera PRO';
            src: url('https://res.cloudinary.com/scentre-group-au/raw/upload/v1558659273/fonts/CeraPRO-Regular.otf') format('woff');
            font-weight: normal;
            font-style: normal;
          }
          
          @font-face {
            font-family: 'Cera PRO';
            src: url('https://res.cloudinary.com/scentre-group-au/raw/upload/v1558659270/fonts/Cera_PRO_Bold.otf') format('woff');
            font-weight: bold;
            font-style: normal;
          }
        `}
        />
        {resetCss && (
          <EmotionGlobal
            styles={`
          ${
            '' /*
            Opinionated:
            Reset list styling to provide a more consistent and
            expected behaviour for developers.

            Why:
            Browsers default to `list-style-position: outside` on ul & ol,
            which means the bullets sit _outside_ the container of the ul. This is
            regardless of box-sizing properties that may be set. Because of this,
            the bullets will fall off the left-side of the container. To counter
            that side-effect, browsers default to inserting left padding on ul &
            ol such that the bullets appear to be back within the container again.

            To make this a bit more predictable and force the developer to think
            about where to place the bullets, we reset the style completely here.

            I (Jess) found it interesting that Chakra does not do any list style
            resets (https://git.io/J3R0R).

            These particluar styles are derived from other popular libraries which
            do a reset on list styles:

            - TailwindCSS: https://git.io/J3R0P
            - Bootstrap: https://getbootstrap.com/docs/5.0/content/reboot/#lists
            - Sanitize.css: https://git.io/J3R0b

            A note on list-style-position:
            Ideally we could use `list-style-position: inside` which would put the
            bullets back inside the container. Unfortuantely, Firefox & Safari
            have a long running issue where a block-level child will then be put
            on a new line (beacuse it incorrectly treats the bullet as a block):
            https://bugzilla.mozilla.org/show_bug.cgi?id=36854
          */
          }
          ol,
          ul {
            list-style: none;
            padding: 0;
          }
        `}
          />
        )}
        {children}
      </ChakraProvider>
    </HubLinkContext.Provider>
  )
}

type ChakraResponsiveObject<T = string> = Partial<Record<string, T>>

export {
  colors,
  sizeScale,
  typographyScale,
  iconSizes,
  lineHeights,
} from './theme'

export {
  useToken,
  useTheme,
  useBreakpoint,
  css,
  forwardRef,
  keyframes,
} from '@chakra-ui/react'
export { isDark, isLight, darken, lighten } from '@chakra-ui/theme-tools'
export { Global } from './global'
export type { As, SystemStyleObject as HubStyleObject } from '@chakra-ui/react'
export * from './utils'
export type StandardSpacings = keyof typeof defaultTheme.space | string
export type StandardSizes = keyof typeof defaultTheme.sizes | string
export type StandardIconSizes = keyof typeof defaultTheme.iconSizes
export type StandardFontSizes = keyof typeof defaultTheme.fontSizes
export type StandardLineHeights = keyof typeof defaultTheme.lineHeights
export type StandardColors = keyof typeof defaultTheme.colors
export type StandardTextAlignment = SystemProps['textAlign']
export type HubResponsiveValue<T> = ResponsiveValue<T>

/**
 * Allow single value, or array of single values, but never object syntax.
 * Why? Our utilities which augment Chakra features can't handle the object
 * syntax. TODO: FIXME so that we _can_ handle the object syntax.
 */
export type HubResponsiveArray<T> = Exclude<
  HubResponsiveValue<T>,
  ChakraResponsiveObject<T>
>

/**
 * Allows the extraction of the non-object style responsive values by inferring
 * the type T from the provided value U
 * Can be used like rowSpan: HubExcludeResponsiveObject<GridItemProps['rowSpan']>
 * This will return a responsive value without the object options
 * We know we want to exclude all functions because the inferring causes primitives
 * and literals to add several functions to the type union
 */
export type HubExcludeResponsiveObject<U> = U extends HubResponsiveValue<
  infer T
>
  ? Exclude<
      // eslint-disable-next-line @typescript-eslint/ban-types
      HubResponsiveValue<Exclude<T, Function | never | undefined>>,
      // eslint-disable-next-line @typescript-eslint/ban-types
      ChakraResponsiveObject<T>
    >
  : never
